import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Collapse, Grid, Link, Typography, useTheme } from "@mui/material";
import Card from "../../common/Card";
import Table from "../../common/CustomTable";
import Button from "../../common/Button";
import CustomIconButton from "../../common/CustomIconButton";
import { DescriptionOutlined, ListAltOutlined, SearchOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import CustomTextField from "../../common/CustomTextField";
import CustomDateRange from "../../common/CustomDateRange";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import { DateObject } from "react-multi-date-picker";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  getPracticeListforFilter,
  usePracticeSlice,
} from "../../../redux/slice/practiceSlice";
import { fetchOrderDocumentsByIds } from "../../../redux/slice/order/OrderSlice";
import {
  AiAuditInfo,
  fetchAiAuditList,
  resetAiAuditList,
  SearchAiAuditInfo,
  useAiAuditReducer,
  fetchAuditReport,
} from "../../../redux/slice/AI/AiAuditSlice";
import { saveSelectionForPage } from "../../../utils/StorageUtils";
import { createListSort } from "../../../utils/listSort";
import {
  showHideLoadingDialog,
  setLoadingDialogConfig,
  resetLoadingDialogConfig,
  setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import FacesheetExtractionPopup from "../aiMedicalAudit/FacesheetExtractionPopup";
import PDFViewer from "../../common/pdf/PDFViewer";
import { selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  AI_SERVICE_NAME,
  PATIENT_LIST_ACTIONS,
  PATIENT_STATUS,
  USER_ROLES,
} from "../../../constants/applicationConstants";
import {
  fetchUsersByUserInput,
  useUserReducer,
} from "../../../redux/slice/userSlice";
import { getFacesheetStatusColor, getStatusColor } from "../../../utils/Utility";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

interface SearchFaceSheet {
  patientName: string;
  practiceIds: number[];
  fromDate: string;
  toDate: string;
  dateRange?: string;
  currentPage: number;
  itemsPerPage: number;
  uploadedByUserIds?: number[];
  serviceName?: string;
  patientStatus?: string[];
  sortBy?: SearchAiAuditInfo["sortBy"];
  sortOrder?: SearchAiAuditInfo["sortOrder"];
}

const ITEMS_PER_PAGE = 10;

const faceSheetListSort = createListSort(
  ["patientName", "practiceName", "fileName", "createdByUserName", "createdAt", "status", "patientStatus"] as const,
  {
    patientNameLink: "patientName",
    practiceName: "practiceName",
    fileName: "fileName",
    createdByUserName: "createdByUserName",
    createdAt: "createdAt",
    status: "status",
    patientStatus: "patientStatus",
  }
);

const columns: Column[] = [
  { id: "patientNameLink", label: "Patient Name" },
  { id: "practiceName", label: "Practice Name" },
  { id: "fileName", label: "File Name" },
  { id: "createdByUserName", label: "Processed By" },
  { id: "createdAt", label: "Processed Date" },
  { id: "status", label: "Facesheet Status" },
  { id: "patientStatus", label: "Patient Status" },
  { id: "action", label: "Action" },
];

// CS - 1790 Create FaceSheet Extraction Tool List Page
const FaceSheetList: React.FC = () => {
  const location = useLocation();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const { control, handleSubmit, setValue, getValues } =
    useForm<SearchFaceSheet>();
  const [showCard, setShowCard] = useState(true);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [isDateRangeChanged, setIsDateRangeChanged] = useState(false);
  const [isClearDateRange, setIsClearDateRange] = useState(false);
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null);
  const [showFacesheetPopup, setShowFacesheetPopup] = useState(false);
  const [selectedFacesheetData, setSelectedFacesheetData] = useState<any>(null);
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    title: string;
    fileName: string;
    base64String: string;
  }>({ isShow: false, title: "", fileName: "", base64String: "" });

  const { practiceByUserRole } = usePracticeSlice();
  const { aiAuditList } = useAiAuditReducer();
  const { userInfo } = useAuthSlice();
  const { fetchUsersByQuery } = useUserReducer();
  const canViewDetails = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessPatientActions", PATIENT_LIST_ACTIONS.VIEW_DETAILS)(state),
  );
  const isAdminUser =
    userInfo.userRole === USER_ROLES.ROLE_ADMIN ||
    userInfo.userRole === USER_ROLES.ROLE_SUPERADMIN;

  const [searchCriteria, setSearchCriteria] = useState<SearchFaceSheet>({
    patientName: "",
    practiceIds: [],
    fromDate: "",
    toDate: "",
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    serviceName: AI_SERVICE_NAME.FACESHEET,
    patientStatus: [],
  });

  // Load active button selection from session storage
  useEffect(() => {
    const saved = sessionStorage.getItem("activeFaceSheetButtonId");
    if (saved) {
      setActiveButtonId(saved);
    }
  }, []);

  // Fetch practice list on mount
  useEffect(() => {
    dispatch(getPracticeListforFilter());
    dispatch(fetchUsersByUserInput({ searchQuery: "", skipLoader: true }));
  }, [dispatch]);

  // Practice dropdown options
  const practiceList = useMemo(() => {
    let options: { label: string; value: any }[] = [];
    if (practiceByUserRole) {
      if (practiceByUserRole?.length > 1) {
        practiceByUserRole.forEach((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      } else if (practiceByUserRole?.length === 1) {
        setValue("practiceIds", [Number(practiceByUserRole[0]?.practiceId)]);
        practiceByUserRole.forEach((item) => {
          options.push({ label: item.practiceName, value: item.practiceId });
        });
      }
    }
    return options;
  }, [practiceByUserRole, setValue]);

  const uploadedByUserList = useMemo(() => {
    if (!isAdminUser || !fetchUsersByQuery) return [];
    return fetchUsersByQuery.map((user) => ({
      label: `${user.firstName} ${user.lastName} (${user.email})`.trim(),
      value: user.userId,
    }));
  }, [fetchUsersByQuery, isAdminUser]);

  const patientStatusOptions = useMemo(
    () => [
      { value: PATIENT_STATUS.ACTIVE, label: "CREATED" },
      { value: PATIENT_STATUS.DRAFTED, label: "DRAFTED" },
      // CS- 1790 Facesheet-only status — not part of the shared PATIENT_STATUS constant
      { value: "CANCELLED", label: "CANCELLED" },
      { value: PATIENT_STATUS.CLOSED, label: "CLOSED" },
    ],
    [],
  );

  const searchFaceSheetList = useCallback(
    (currentPage: number, obj?: SearchFaceSheet) => {
      let data: SearchAiAuditInfo = {
        patientName: obj?.patientName || "",
        practiceIds: obj?.practiceIds || [],
        fromDate: obj?.fromDate || "",
        toDate: obj?.toDate || "",
        currentPage: currentPage,
        downloadState: false,
        itemsPerPage: ITEMS_PER_PAGE,
        uploadedByUserIds: obj?.uploadedByUserIds || [],
        serviceName: AI_SERVICE_NAME.FACESHEET,
        patientStatus: obj?.patientStatus || [],
        ...faceSheetListSort.pick(obj),
      };
      setValue("currentPage", currentPage);
      setSearchCriteria(data);

      if (userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU) {
        dispatch(fetchAiAuditList(data));
      }
    },
    [setValue, dispatch, userInfo.userRole],
  );

  useEffect(() => {
    setShowCard(true);
    searchFaceSheetList(1);
  }, [searchFaceSheetList]);

  useEffect(() => {
    if (location.state && location.state.faceSheetListSearchCriteria) {
      let searchCriteria: SearchFaceSheet =
        location.state.faceSheetListSearchCriteria;
      setSearchCriteria(searchCriteria);
      setValue("patientName", searchCriteria?.patientName || "");
      setValue("practiceIds", searchCriteria?.practiceIds || []);
      setValue("fromDate", searchCriteria?.fromDate || "");
      setValue("toDate", searchCriteria?.toDate || "");
      if (searchCriteria.fromDate && searchCriteria.toDate) {
        setValue(
          "dateRange",
          `${searchCriteria.fromDate} - ${searchCriteria.toDate}`,
        );
      }
      if (isAdminUser) {
        setValue("uploadedByUserIds", searchCriteria?.uploadedByUserIds || []);
      }
      setValue("patientStatus", searchCriteria?.patientStatus || []);
      searchFaceSheetList(searchCriteria?.currentPage, searchCriteria);
    }
  }, [location.state, searchFaceSheetList, setValue, isAdminUser]);

  useEffect(() => {
    return () => {
      dispatch(resetAiAuditList());
    };
  }, [dispatch]);

  const onSubmit = (data: SearchFaceSheet) => {
    if (!isDateRangeChanged) {
      setIsClearDateRange(true);
    }
    const submitObj: SearchFaceSheet = {
      patientName: data?.patientName || "",
      practiceIds: data?.practiceIds || [],
      fromDate: isDateRangeChanged ? getValues("fromDate") || "" : "",
      toDate: isDateRangeChanged ? getValues("toDate") || "" : "",
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      uploadedByUserIds: isAdminUser ? data?.uploadedByUserIds || [] : [],
      serviceName: AI_SERVICE_NAME.FACESHEET,
      patientStatus: data?.patientStatus || [],
      ...faceSheetListSort.pick(searchCriteria),
    };

    navigate("/facesheet", {
      state: { faceSheetListSearchCriteria: submitObj },
    });
  };

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1,
    };
    setSearchCriteria(data);
    navigate("/facesheet", {
      state: { faceSheetListSearchCriteria: data },
    });
  };

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = faceSheetListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/facesheet", {
      state: { faceSheetListSearchCriteria: data },
    });
  };

  const handleDateRangeChange = (
    fromDate: DateObject | null,
    toDate: DateObject | null,
  ) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("fromDate", fromDateString ? fromDateString : "");
    setValue("toDate", toDateString ? toDateString : "");
    setIsDateRangeChanged(true);
  };

  const handleActionButtonClick = useCallback(
    (buttonId: string, originalClick: () => void) => {
      const currentPageKey = "faceSheetList";
      saveSelectionForPage(currentPageKey, buttonId);
      setActiveButtonId(buttonId);
      sessionStorage.setItem("activeFaceSheetButtonId", buttonId);
      originalClick();
    },
    [],
  );

  const handleViewFaceSheet = useCallback(
    (row: AiAuditInfo) => {
      handleActionButtonClick(
        `faceSheetListActionButton-${row.auditId}`,
        () => {
          navigate("/facesheet_details", {
            state: { auditId: row.auditId },
          });
        },
      );
    },
    [handleActionButtonClick, navigate],
  );

  const goToPatientDetails = useCallback(
    (row: any) => {
      handleActionButtonClick(
        `faceSheetListPatientDetailsButton-${row.aiAuditDataId}`,
        () =>
          navigate("/patientDetails", {
            state: {
              patientDetails: {
                patientId: row.patientId,
                patientName: row.patientName,
              },
            },
          }),
      );
    },
    [handleActionButtonClick, navigate],
  );

  const handleOpenUploadedDocument = useCallback(
    async (row: any) => {
      if (!row?.documentId) {
        dispatch(
          setSingleAlertObj({
            message: "Uploaded document is not available.",
            alertType: "info",
          }),
        );
        return;
      }
      const result = await dispatch(fetchOrderDocumentsByIds(Number(row.documentId)));
      const documentData: any = result.payload;
      if (documentData?.content) {
        const docName = documentData.documentName || row.fileName || "Document";
        setShowPDF({
          isShow: true,
          title: docName,
          fileName: docName,
          base64String: documentData.content,
        });
      } else {
        dispatch(
          setSingleAlertObj({
            message: "Document not found!",
            alertType: "info",
          }),
        );
      }
    },
    [dispatch],
  );

  const handleOpenFacesheetReport = useCallback(
    (row: AiAuditInfo) => {
      if (!row.auditId) return;

      dispatch(
        setLoadingDialogConfig({
          variant: "aiMedicalAudit",
          title: "Loading Report",
          message: "Fetching facesheet report data...",
        }),
      );
      dispatch(showHideLoadingDialog(true));

      dispatch(fetchAuditReport(row.auditId)).then((response: any) => {
        dispatch(showHideLoadingDialog(false));
        dispatch(resetLoadingDialogConfig());

        if (response.meta.requestStatus === "fulfilled") {
          const responseJson =
            response.payload?.data?.responseJson || response.payload?.data;
          setSelectedFacesheetData({
            ...responseJson,
            file_name: row.fileName,
          });
          setShowFacesheetPopup(true);
        } else {
          dispatch(
            setSingleAlertObj({
              message: "Failed to fetch facesheet report.",
              alertType: "error",
            }),
          );
        }
      });
    },
    [dispatch],
  );

  const formattedFaceSheetRows = useMemo(() => {
    let formatRows: any[] = [];
    aiAuditList?.searchAuditData?.content?.forEach((row) => {
      const canOpenPatientDetails =
        canViewDetails && !!row.patientId && row.patientStatus !== "CANCELLED";
      let formatRow = {
        ...row,
        rawPatientStatus: row.patientStatus,
        patientName: row.patientName || "-",
        practiceName: row.practiceName || "-",
        patientNameLink: canOpenPatientDetails && row.patientName ? (
          <Link
            component="button"
            underline="hover"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              goToPatientDetails(row);
            }}
            color={"#00B8D9"}
          >
            {row.patientName}
          </Link>
        ) : (
          row.patientName || "-"
        ),
        auditIdLink: row.auditId ? (
          <Link
            component="button"
            underline="hover"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              handleActionButtonClick(
                `faceSheetListActionButton-${row.auditId}`,
                () => handleViewFaceSheet(row),
              );
            }}
            color={"#00B8D9"}
          >
            {row.auditId}
          </Link>
        ) : (
          row.auditId
        ),
        patientStatus: row.patientStatus ? (
          <Typography
            sx={{
              color: getStatusColor(row.patientStatus),
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            {row.patientStatus}
          </Typography>
        ) : (
          "-"
        ),
        status: row.status ? (
          <Typography
            sx={{
              color: getFacesheetStatusColor(row.status),
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            {row.status}
          </Typography>
        ) : (
          "-"
        ),
      };
      formatRows.push(formatRow);
    });
    return formatRows;
  }, [aiAuditList, handleActionButtonClick, handleViewFaceSheet, canViewDetails, goToPatientDetails]);

  // Action column renderer
  const actionComponent = (row: any): JSX.Element => {
    const hidePatientActions = !row.patientId || row.rawPatientStatus === "CANCELLED";
    const hidePatientDetails = !canViewDetails || hidePatientActions;
    return (
      <>
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Report"
          color="success"
          size="small"
          variant="outlined"
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(
              `faceSheetListViewButton-${row.aiAuditDataId}`,
              () => handleOpenFacesheetReport(row),
            );
          }}
        >
          <VisibilityOutlined fontSize="small" />
        </CustomIconButton>

        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Patient Details"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: hidePatientDetails ? "none" : "" }}
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            goToPatientDetails(row);
          }}
        >
          <ListAltOutlined fontSize="small" />
        </CustomIconButton>

        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Uploaded Facesheet"
          color="warning"
          size="small"
          variant="outlined"
          onClick={(e?: React.MouseEvent) => {
            if (e) e.stopPropagation();
            handleActionButtonClick(
              `faceSheetListUploadedDocumentButton-${row.aiAuditDataId}`,
              () => handleOpenUploadedDocument(row),
            );
          }}
        >
          <DescriptionOutlined fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  // ── Design tokens ──────────────
  const fsBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const fsPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const fsHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const fsCardSx = {
    borderRadius: "14px",
    border: `1px solid ${fsBorderColor}`,
    backgroundColor: fsPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...fsCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "0 38px 0 12px !important" },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": { paddingLeft: "12px !important", paddingRight: "12px !important" },
    "& .MuiAutocomplete-endAdornment": { top: "50% !important", transform: "translateY(-50%)", right: "8px !important" },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": { borderColor: `${modalTokens.inputBorder} !important` },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${modalTokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)" },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": {
      color: modalTokens.textPrimary,
      backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
    },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: "#376c9f !important",
      "& fieldset": { borderColor: "#7fb1df !important" },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitTextFillColor: "#ffffff",
      caretColor: "#ffffff",
      WebkitBoxShadow: "0 0 0 100px #376c9f inset",
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
      color: modalTokens.accent,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
      fontWeight: 600,
    },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
    "& .rmdp-container": { width: "100%", height: "42px", overflow: "visible", zIndex: 30 },
    "& .rmdp-wrapper, & .rmdp-calendar, & .rmdp-day-picker": { height: "auto !important", minHeight: "unset !important", overflow: "visible" },
    "& .rmdp-wrapper": { zIndex: "1600 !important", position: "absolute" },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
    "& .MuiSvgIcon-root": { color: "#fff" },
  };

  const filterToggleBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  const fsTableSx = {
    "& .MuiTableContainer-root": { borderRadius: "12px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 1.15,
      fontSize: "0.84rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${fsHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type .MuiLink-root": {
      color: "#00B8D9 !important",
      fontWeight: 600,
      textDecorationColor: "rgba(0,184,217,0.45)",
    },
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type .MuiLink-root:hover": {
      color: "#22d3ee !important",
      textDecorationColor: "#22d3ee",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: modalTokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor: fsBorderColor,
      color: modalTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: modalTokens.accent,
        borderColor: modalTokens.accent,
      },
    },
  };
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={3}>
                    <CustomTextField
                      controllerName="patientName"
                      label="Patient Name"
                      control={control}
                      placeholder="Enter patient name"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown
                      name="practiceIds"
                      label="Practice Name"
                      control={control}
                      options={practiceList}
                      disabled={practiceList?.length === 1}
                      placeholderText="Select practice"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <CustomDateRange
                      label="Processed Date Range"
                      placeholder="MM/DD/YYYY - MM/DD/YYYY"
                      control={control}
                      controllerName="dateRange"
                      onChange={handleDateRangeChange}
                      onClear={isClearDateRange}
                    />
                  </Grid>
                  {isAdminUser && (
                    <Grid item xs={12} sm={3}>
                      <CustomMultipleDropDown
                        name="uploadedByUserIds"
                        label="Processed By"
                        control={control}
                        options={uploadedByUserList}
                        placeholderText="Select user"
                        size="small"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown
                      name="patientStatus"
                      label="Patient Status"
                      control={control}
                      options={patientStatusOptions}
                      sortOptions={false}
                      placeholderText="Select status"
                      size="small"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...fsCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              spacing={1.2}
            >
              <Grid item xs={12} md>
                <Typography variant="h3" fontWeight={800} color="text.primary">
                  Facesheet List
                </Typography>
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={filterToggleBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>
            </Grid>
            <Table
              columns={columns}
              rows={formattedFaceSheetRows || []}
              pageNumber={handlePagination}
              totalPages={aiAuditList?.searchAuditData?.totalPages}
              totalElementText={
                "Records: " +
                (aiAuditList?.searchAuditData?.totalElements
                  ? aiAuditList?.searchAuditData?.totalElements
                  : "0")
              }
              emptyText="No FaceSheet Records Found"
              currentPageNumber={
                getValues("currentPage") ? getValues("currentPage") - 1 : 0
              }
              showMenu={true}
              sortableColumns={faceSheetListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={faceSheetListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              openMenuOnRowClick={true}
              uniqueValue="aiAuditDataId"
              currentPageKey="faceSheetList"
              activeButtonId={activeButtonId}
              clickableCards={false}
              actionComponent={actionComponent}
              sx={fsTableSx}
            />
          </Card>
        </Box>
      </Box>

      {showFacesheetPopup && (
        <FacesheetExtractionPopup
          open={showFacesheetPopup}
          onClose={() => {
            setShowFacesheetPopup(false);
            setSelectedFacesheetData(null);
          }}
          initialFacesheetData={selectedFacesheetData}
        />
      )}

      {showPDF.isShow && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, title: "", fileName: "", base64String: "" })
          }
          title={showPDF.title}
          fileName={showPDF.fileName}
          base64String={showPDF.base64String}
        />
      )}
    </>
  );
};

export default FaceSheetList;